import type {
  BridgeInsight,
  ComponentsResponse,
  GraphComponent,
  TopEntitiesResponse,
} from '@/services/api/graph'
import { type ScoreBand, scoreBand } from './primitives/score'

// ─── KPI summary ─────────────────────────────────────────────────────────────

export interface KpiSummary {
  entities: number
  clusters: number
  bridges: number
  suspects: number
  topScore: number | null
  topScoreEntityName: string | null
  biggestCluster: GraphComponent | null
}

export function selectKpiSummary(
  components: ComponentsResponse | null,
  bridges: BridgeInsight[],
  topEntities: TopEntitiesResponse | null
): KpiSummary {
  const comps = components?.components ?? []
  const entities = comps.reduce((sum, c) => sum + c.size, 0)
  const clusters = comps.length
  const bridgeCount = bridges.length

  let suspects = 0
  let topScore: number | null = null
  let topScoreEntityName: string | null = null
  for (const g of topEntities?.entity_types ?? []) {
    for (const e of g.top_entities) {
      if (e.is_suspect === true) suspects += 1
      if (e.score != null && (topScore == null || e.score > topScore)) {
        topScore = e.score
        topScoreEntityName = e.name
      }
    }
  }

  const biggestCluster =
    comps.length === 0 ? null : comps.reduce((a, b) => (a.size >= b.size ? a : b))

  return {
    entities,
    clusters,
    bridges: bridgeCount,
    suspects,
    topScore,
    topScoreEntityName,
    biggestCluster,
  }
}

// ─── Cluster composition ─────────────────────────────────────────────────────

export const ENTITY_TYPE_COLOR_ORDER: Array<{ type: string; cssVar: string }> = [
  { type: 'PhoneNumber', cssVar: 'var(--chart-1)' },
  { type: 'Person', cssVar: 'var(--chart-2)' },
  { type: 'Location', cssVar: 'var(--chart-3)' },
  { type: 'Vehicle', cssVar: 'var(--chart-4)' },
  { type: 'FuelStation', cssVar: 'var(--chart-4)' },
  { type: 'SocialAccount', cssVar: 'var(--chart-4)' },
  { type: 'Device', cssVar: 'var(--chart-5)' },
]

const OTHER_COLOR = 'var(--chart-5)'

export interface CompositionSegment {
  type: string
  share: number
  color: string
}

export interface ClusterComposition {
  comp_id: number
  size: number
  sampleCount: number
  segments: CompositionSegment[]
}

export function cssVarForType(type: string): string {
  const match = ENTITY_TYPE_COLOR_ORDER.find(e => e.type === type)
  return match ? match.cssVar : OTHER_COLOR
}

export function selectClusterComposition(
  components: ComponentsResponse | null,
  opts: { topN?: number } = {}
): ClusterComposition[] {
  const topN = opts.topN ?? 8
  const comps = components?.components ?? []
  const sorted = [...comps].sort((a, b) => b.size - a.size).slice(0, topN)

  return sorted.map(c => {
    const counts = new Map<string, number>()
    for (const s of c.sample_entities) {
      const t = s.type || 'Other'
      counts.set(t, (counts.get(t) ?? 0) + 1)
    }
    const sampleCount = c.sample_entities.length
    const orderedTypes = [
      ...ENTITY_TYPE_COLOR_ORDER.map(e => e.type),
      ...Array.from(counts.keys()).filter(t => !ENTITY_TYPE_COLOR_ORDER.some(e => e.type === t)),
    ]
    const seen = new Set<string>()
    const segments: CompositionSegment[] = []
    let otherCount = 0
    for (const type of orderedTypes) {
      if (seen.has(type)) continue
      seen.add(type)
      const count = counts.get(type) ?? 0
      if (count === 0) continue
      const known = ENTITY_TYPE_COLOR_ORDER.some(e => e.type === type)
      if (known) {
        segments.push({
          type,
          share: sampleCount === 0 ? 0 : count / sampleCount,
          color: cssVarForType(type),
        })
      } else {
        otherCount += count
      }
    }
    if (otherCount > 0) {
      segments.push({
        type: 'Other',
        share: sampleCount === 0 ? 0 : otherCount / sampleCount,
        color: OTHER_COLOR,
      })
    }
    return { comp_id: c.comp_id, size: c.size, sampleCount, segments }
  })
}

// ─── Score distribution buckets ──────────────────────────────────────────────

export interface ScoreBucket {
  bucket: string
  lower: number
  upper: number
  count: number
  band: ScoreBand
}

export function selectScoreBuckets(topEntities: TopEntitiesResponse | null): ScoreBucket[] {
  const buckets: ScoreBucket[] = []
  for (let i = 0; i < 10; i += 1) {
    const lower = i * 10
    const upper = lower + 10
    const mid = lower + 5
    buckets.push({
      bucket: `${lower}–${upper}`,
      lower,
      upper,
      count: 0,
      band: scoreBand(mid),
    })
  }
  for (const g of topEntities?.entity_types ?? []) {
    for (const e of g.top_entities) {
      if (e.score == null) continue
      const clamped = Math.max(0, Math.min(100, e.score))
      const idx = clamped === 100 ? 9 : Math.floor(clamped / 10)
      buckets[idx].count += 1
    }
  }
  return buckets
}

// ─── Bridge groups ───────────────────────────────────────────────────────────

export interface BridgeGroup {
  key: string
  componentA: number
  componentB: number
  bridges: BridgeInsight[]
}

export function selectBridgeGroups(bridges: BridgeInsight[]): BridgeGroup[] {
  const map = new Map<string, BridgeGroup>()
  for (const b of bridges) {
    const a = Math.min(b.component_a, b.component_b)
    const bb = Math.max(b.component_a, b.component_b)
    const key = `${a}-${bb}`
    const group = map.get(key) ?? {
      key,
      componentA: a,
      componentB: bb,
      bridges: [],
    }
    group.bridges.push(b)
    map.set(key, group)
  }
  return Array.from(map.values()).sort((x, y) => y.bridges.length - x.bridges.length)
}

// ─── Cluster label helpers ───────────────────────────────────────────────────

/**
 * Map a cluster comp_id to its human label ("Cluster A", "Cluster B", …)
 * based on its rank by size in the full components list.
 */
export function buildClusterLabelMap(components: ComponentsResponse | null): Map<number, string> {
  const sorted = [...(components?.components ?? [])].sort((a, b) => b.size - a.size)
  const map = new Map<number, string>()
  sorted.forEach((c, idx) => {
    map.set(c.comp_id, `Cluster ${String.fromCharCode(65 + idx)}`)
  })
  return map
}
